// CHG-2545 / DRIFT-934 — fetch the live password policy on mount and
// expose `min_length` so the v2 auth pages stop hardcoding a stale
// `password.length < 8` literal. The backend
// (services/password_policy.py:DEFAULT_MIN_LENGTH=12) enforces an
// org-configurable minimum; before this hook, ResetPasswordPage,
// AcceptInvitePage, and RegisterFromEmailPage each carried a literal 8,
// so passwords of 8-11 chars passed the v2 client check and were then
// rejected by the BE with a confusing 400 (parity/UX defect).
//
// Ports the v1 `web/dashboard/src/hooks/usePasswordPolicy.ts` pattern.
// The endpoint (GET /settings/password-policy, operation_id
// getPasswordPolicy) is unauthenticated — safe to call from pre-session
// auth surfaces.

import { useEffect, useState } from "react";
import { getPasswordPolicy, type PasswordPolicyDescriptor } from "../../api/settings";

// Conservative fallback if the endpoint is unreachable (e.g. a transient
// network blip). Matches services/password_policy.py DEFAULT_MIN_LENGTH=12
// so a stale/failed fetch never produces a LOOSER FE check than the BE
// actually applies — the FE guard stays in lockstep with the shipped
// backend default and never re-introduces the 8-char gap this drift fixes.
export const PASSWORD_POLICY_FALLBACK: PasswordPolicyDescriptor = {
  min_length: 12,
  require_uppercase: false,
  require_lowercase: false,
  require_digit: false,
  require_symbol: false,
  forbid_equals_email: true,
  forbid_contains_email_localpart: true,
  contains_email_localpart_mode: "reject",
  rules: [],
};

export interface UsePasswordPolicyResult {
  policy: PasswordPolicyDescriptor;
  loading: boolean;
}

export function usePasswordPolicy(): UsePasswordPolicyResult {
  const [policy, setPolicy] = useState<PasswordPolicyDescriptor>(PASSWORD_POLICY_FALLBACK);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getPasswordPolicy()
      .then((p) => {
        if (!cancelled && p) setPolicy(p);
      })
      .catch(() => {
        // Keep PASSWORD_POLICY_FALLBACK — the BE will surface the specific
        // policy violation on submit via the error detail; the FE check is
        // ergonomic (proactive), not authoritative.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { policy, loading };
}
